import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, Sparkles } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const flagStyle = (flag) =>
  flag?.startsWith("Critical")
    ? "border-[#C92A2A] bg-[#FEF2F2] text-[#C92A2A]"
    : flag === "High" || flag === "Low"
    ? "border-[#B45309] bg-[#FFFBEB] text-[#B45309]"
    : "border-[#E5E7EB]";

const classify = (p, raw) => {
  if (raw === undefined || String(raw).trim() === "") return "";
  const v = parseFloat(String(raw).replace(/[^\d.-]/g, ""));
  if (Number.isNaN(v)) return "Reported";
  if (p.critical_low !== null && p.critical_low !== undefined && v < p.critical_low) return "Critical Low";
  if (p.critical_high !== null && p.critical_high !== undefined && v > p.critical_high) return "Critical High";
  if (p.ref_low !== null && p.ref_low !== undefined && v < p.ref_low) return "Low";
  if (p.ref_high !== null && p.ref_high !== undefined && v > p.ref_high) return "High";
  return "Normal";
};

export const ResultsGrid = ({ testName, sampleId, analyzer, results = {}, onChange }) => {
  const [panel, setPanel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState("");

  const load = useCallback(async () => {
    if (!testName) { setPanel(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/panels/${encodeURIComponent(testName)}`);
      setPanel(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [testName]);

  useEffect(() => { load(); }, [load]);

  const applyFeed = (feed) => {
    const next = { ...results };
    (panel?.parameters || []).forEach((p) => {
      if (feed.results?.[p.name] !== undefined) next[p.name] = String(feed.results[p.name]);
    });
    onChange(next);
    toast.success(`${Object.keys(feed.results || {}).length} values loaded from ${feed.analyzer || "analyzer"}`);
  };

  const pull = async () => {
    if (!sampleId) return toast.error("Enter the Sample ID first");
    setFetching("pull");
    try {
      const { data } = await api.get(`/instrument/results/${encodeURIComponent(sampleId)}`);
      applyFeed(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setFetching(""); }
  };

  const simulate = async () => {
    if (!sampleId) return toast.error("Enter the Sample ID first");
    setFetching("sim");
    try {
      const { data } = await api.post("/instrument/simulate", {
        sample_id: sampleId, test_name: testName, analyzer: analyzer || "",
      });
      applyFeed(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setFetching(""); }
  };

  if (!testName) {
    return (
      <div className="sm:col-span-2 rounded-md border border-dashed border-[#E5E7EB] p-4 text-sm text-[#6B7280]"
        data-testid="results-grid-empty">
        Pick a Test above and its result template will appear here for value entry.
      </div>
    );
  }

  const params = panel?.parameters || [];

  return (
    <div className="sm:col-span-2 rounded-md border border-[#E5E7EB] p-4" data-testid="results-grid">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-heading font-bold text-sm text-[#111827]">{testName} — result template</p>
          <p className="text-xs text-[#6B7280] mt-0.5">
            {params.length
              ? `${params.length} parameters. Values outside the reference range are highlighted; critical values raise an alert automatically.`
              : "No template yet for this test — add one under Test Templates."}
          </p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={pull} data-testid="fetch-analyzer-btn"
            disabled={!!fetching}>
            {fetching === "pull" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5 text-[#002FA7]" />}
            Fetch from analyzer
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={simulate} data-testid="simulate-analyzer-btn"
            disabled={!!fetching}>
            {fetching === "sim" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5 text-[#B45309]" />}
            Simulate analyzer feed (demo)
          </Button>
        </div>
      </div>

      {loading && <p className="text-sm text-[#6B7280] mt-3">Loading template…</p>}

      {!loading && params.length > 0 && (
        <div className="mt-3 grid sm:grid-cols-2 gap-2">
          {params.map((p) => {
            const raw = results[p.name] ?? "";
            const flag = classify(p, raw);
            return (
              <div key={p.name} className="flex items-center gap-2" data-testid={`result-row-${p.name}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-[#111827] truncate" title={p.name}>{p.name}</p>
                  <p className="text-[10px] text-[#6B7280]">
                    {p.ref_low !== null && p.ref_high !== null ? `Ref ${p.ref_low} – ${p.ref_high}` : "No range"}
                    {p.unit ? ` ${p.unit}` : ""}
                  </p>
                </div>
                <Input value={raw} onChange={(e) => onChange({ ...results, [p.name]: e.target.value })}
                  data-testid={`result-input-${p.name}`} className={`h-9 w-28 text-sm ${flagStyle(flag)}`} />
                <span className={`w-20 text-[10px] uppercase tracking-[0.08em] ${
                  flag.startsWith("Critical") ? "text-[#C92A2A] font-bold" : flag === "High" || flag === "Low" ? "text-[#B45309]" : "text-[#6B7280]"}`}
                  data-testid={`result-flag-${p.name}`}>
                  {flag === "Normal" ? "" : flag}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
